import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  Building2,
  Search,
  Eye,
  Users,
  CheckCircle,
  Clock,
  RefreshCw,
  Filter,
  Edit3,
  Trash2,
  Save,
  X,
  ArrowRight
} from 'lucide-react'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import { adminApi, AdminExchange } from '../../services/api/admin'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'
import { useConfirm } from '../../contexts/ConfirmProvider'

export const AdminExchangesPage: React.FC = () => {
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const [exchanges, setExchanges] = useState<AdminExchange[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedExchange, setSelectedExchange] = useState<AdminExchange | null>(null)
  const [editingExchange, setEditingExchange] = useState<AdminExchange | null>(null)
  const [editForm, setEditForm] = useState<{ name: string; status: string }>({ name: '', status: '' })

  useEffect(() => {
    if (!currentUser?.isAdmin) {
      navigate('/app')
      return
    }
    loadExchanges()
  }, [currentUser, navigate, page, searchQuery, statusFilter])

  const loadExchanges = async () => {
    try {
      setIsLoading(true)
      const response = await adminApi.getExchanges({
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        page,
        limit: 20
      })
      setExchanges(response.exchanges)
      setTotalPages(response.totalPages)
    } catch (error) {
      console.error('Failed to load exchanges:', error)
      toast.error('Failed to load exchanges')
    } finally {
      setIsLoading(false)
    }
  }

  const handleViewExchange = async (exchangeId: string) => {
    try {
      const exchangeDetails = await adminApi.getExchange(exchangeId)
      setSelectedExchange(exchangeDetails as AdminExchange)
    } catch (error) {
      toast.error('Failed to load exchange details')
    }
  }

  const handleEditExchange = (exchange: AdminExchange) => {
    setEditingExchange(exchange)
    setEditForm({ name: exchange.name, status: exchange.status })
  }

  const handleSaveExchange = async () => {
    if (!editingExchange) return

    try {
      await adminApi.updateExchange(editingExchange.id, editForm)
      toast.success('Exchange updated successfully')
      setEditingExchange(null)
      loadExchanges()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update exchange')
    }
  }

  const handleDeleteExchange = async (exchangeId: string) => {
    const ok = await confirm({
      title: 'Delete exchange?',
      description: 'This action cannot be undone.',
      confirmText: 'Delete exchange',
      destructive: true,
      icon: Trash2,
      iconColor: 'rose',
    })
    if (!ok) return

    try {
      await adminApi.deleteExchange(exchangeId)
      toast.success('Exchange deleted successfully')
      loadExchanges()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete exchange')
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Active
          </span>
        )
      case 'pending':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </span>
        )
      case 'completed':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Completed
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
            {status}
          </span>
        )
    }
  }

  if (isLoading && exchanges.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Building2 className="h-8 w-8 text-primary" />
            Exchange Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage all 1031 exchanges across the platform
          </p>
        </div>
        <Button onClick={loadExchanges} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search exchanges by name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setPage(1)
              }}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setPage(1)
              }}
              className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Exchanges Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Exchange
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {exchanges.map((exchange) => (
                <tr key={exchange.id} className="hover:bg-secondary/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingExchange?.id === exchange.id ? (
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full"
                      />
                    ) : (
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-primary" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-foreground">
                            {exchange.name}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            ID: {exchange.id}
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingExchange?.id === exchange.id ? (
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        className="px-3 py-1 border border-input rounded-md bg-background text-foreground text-sm"
                      >
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    ) : (
                      getStatusBadge(exchange.status)
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {exchange.owner ? (
                      <div className="text-sm text-foreground">
                        <div>{exchange.owner.firstName} {exchange.owner.lastName}</div>
                        <div className="text-xs text-muted-foreground">
                          {exchange.owner.email}
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">No owner</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      {exchange.userCount}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                    {exchange.account?.name || exchange.accountName || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {new Date(exchange.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {editingExchange?.id === exchange.id ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSaveExchange}
                            className="text-green-600 hover:text-green-700 dark:text-green-400"
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingExchange(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewExchange(exchange.id)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditExchange(exchange)}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteExchange(exchange.id)}
                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-border flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Exchange Details Modal */}
      {selectedExchange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-foreground">
                  Exchange Details
                </h2>
                <Button variant="outline" onClick={() => setSelectedExchange(null)}>
                  Close
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Name</label>
                  <p className="text-foreground">{selectedExchange.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <div className="mt-2">{getStatusBadge(selectedExchange.status)}</div>
                </div>
                {selectedExchange.owner && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Owner</label>
                    <p className="text-foreground">
                      {selectedExchange.owner.firstName} {selectedExchange.owner.lastName}
                    </p>
                    <p className="text-sm text-muted-foreground">{selectedExchange.owner.email}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Users</label>
                  <p className="text-foreground">{selectedExchange.userCount}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Account</label>
                  <p className="text-foreground">
                    {selectedExchange.account?.name || selectedExchange.accountName || 'N/A'}
                  </p>
                </div>
                <div className="pt-4 border-t border-border">
                  <Link to={`/app?exchange=${selectedExchange.id}`}>
                    <Button className="w-full">
                      View Exchange in App
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created</label>
                  <p className="text-foreground">{new Date(selectedExchange.createdAt).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}


